import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PageHeader from "./PageHeader";
import BaZiFenXiPage from "./BaZiFenXi";
import AiZhiLingPage from "./AiZhiLing";
import EditPersonDialog from "./EditPersonDialog";
import { getGanZhiColor, getIsYang } from "../StyleControl";
import dayjs from "dayjs";
import {
  get_people,
  post_paipan,
  delete_people,
} from "../../client/paipan";
import { enrichBaZiData } from "../../utils/baziCalculator";

export default function RecordsPage({
  user,
  mingPanTrigger,
  recordsResetKey,
  initialMingPanData,
  aiZhiLingState,
  setAiZhiLingState,
  onLoginClick,
  onSubPageChange,
  onMemberClick,
}) {
  const [selectedData, setSelectedData] = useState(null);
  const [detailTab, setDetailTab] = useState("mingpan");
  const [peopleList, setPeopleList] = useState([]);
  const [loading, setLoading] = useState(false);


  // 更多操作菜单状态
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPerson, setMenuPerson] = useState(null);

  // 编辑弹窗状态
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPerson, setEditPerson] = useState(null);

  // 删除确认状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePerson, setDeletePerson] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 排盘后跳转到命盘详情
  useEffect(() => {
    if (initialMingPanData && mingPanTrigger > 0) {
      setSelectedData(initialMingPanData);
      setDetailTab("mingpan");
    }
  }, [mingPanTrigger, initialMingPanData]);

  // 点击"记录"Tab 回到列表
  useEffect(() => {
    if (recordsResetKey > 0) {
      setSelectedData(null);
      setDetailTab("mingpan");
    }
  }, [recordsResetKey]);

  // 通知父组件当前是否在子页面（命盘详情）
  useEffect(() => {
    const inSubPage = Boolean(selectedData);
    onSubPageChange?.(inSubPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData]);

  // 登录态变化时加载通讯录
  useEffect(() => {
    loadPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 排盘后自动刷新通讯录（新增人物后）
  useEffect(() => {
    if (mingPanTrigger > 0) {
      loadPeople();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mingPanTrigger]);

  async function loadPeople() {
    setLoading(true);
    if (user) {
      const res = await get_people();
      if (res && res.list) {
        setPeopleList(res.list);
      }
    } else {
      const records = JSON.parse(localStorage.getItem("bazi_anonymous_records") || "[]");
      setPeopleList(records);
    }
    setLoading(false);
  }

  function handleSelectPerson(person) {
    setDetailTab("mingpan");
    const data = person.result ? { ...person, ...person.result } : person;
    if (data.ri_gan) {
      enrichBaZiData(data);
      setSelectedData(data);
    } else {
      // 旧数据没有 result，尝试重新排盘
      const paipanData = {
        name: person.name,
        gender: person.gender,
        birthday_str: person.birthday_str,
        use_zaozi: !!person.use_zaozi,
        use_true_solar: !!person.use_true_solar,
        longitude: person.longitude,
        birthplace: person.birthplace,
      };
      post_paipan(paipanData).then((res) => {
        if (res) {
          setSelectedData(res);
        }
      });
    }
  }

  function handleBackToList() {
    setSelectedData(null);
    setDetailTab("mingpan");
  }

  // 打开更多菜单
  function handleOpenMenu(e, person) {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuPerson(person);
  }

  function handleCloseMenu() {
    setMenuAnchor(null);
    setMenuPerson(null);
  }

  function handleEditClick() {
    setEditPerson(menuPerson);
    setEditDialogOpen(true);
    handleCloseMenu();
  }

  function handleDeleteClick() {
    setDeletePerson(menuPerson);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  }

  async function handleConfirmDelete() {
    if (!deletePerson) return;
    if (user) {
      setDeleteLoading(true);
      const res = await delete_people(deletePerson.id);
      if (res && res.message === "删除成功") {
        await loadPeople();
        setDeleteDialogOpen(false);
        setDeletePerson(null);
      } else {
        alert(res?.message || "删除失败，请重试");
      }
      setDeleteLoading(false);
    } else {
      // 匿名用户从 localStorage 删除
      const records = JSON.parse(localStorage.getItem("bazi_anonymous_records") || "[]");
      const filtered = records.filter((r) => r.id !== deletePerson.id);
      localStorage.setItem("bazi_anonymous_records", JSON.stringify(filtered));
      loadPeople();
      setDeleteDialogOpen(false);
      setDeletePerson(null);
    }
  }

  // 命盘详情 + AI指令 Tab 视图
  if (selectedData) {
    return (
      <Box sx={{ width: "100%" }}>
        <AppBar
          position="static"
          elevation={0}
          sx={(theme) => ({
            bgcolor: "background.paper",
            color: "#5C4D3C",
            [theme.breakpoints.up("md")]: {
              position: "relative",
              left: "50%",
              transform: "translateX(-50%)",
              width: "100vw",
            },
          })}
        >
          <Toolbar
            sx={{
              justifyContent: "flex-start",
              minHeight: 48,
              px: 1,
              gap: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={handleBackToList}
              edge="start"
              sx={{ color: "#5C4D3C" }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <Tabs
                value={detailTab}
                onChange={(e, v) => setDetailTab(v)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ minHeight: 48 }}
              >
                <Tab
                  value="mingpan"
                  label="命盘"
                  sx={{ textTransform: "none", fontWeight: "bold", minHeight: 48 }}
                />
                <Tab
                  value="aizhiling"
                  label="AI指令"
                  sx={{ textTransform: "none", fontWeight: "bold", minHeight: 48 }}
                />
              </Tabs>
            </Box>
          </Toolbar>
        </AppBar>
        <Box sx={{ display: detailTab === "mingpan" ? "block" : "none" }}>
          <BaZiFenXiPage
            data={selectedData}
            onSwitchToAiZhiLing={() => setDetailTab("aizhiling")}
          />
        </Box>
        <Box sx={{ display: detailTab === "aizhiling" ? "block" : "none" }}>
          <AiZhiLingPage
            data={selectedData}
            state={aiZhiLingState}
            setState={setAiZhiLingState}
            user={user}
            onMemberClick={onMemberClick}
          />
        </Box>
      </Box>
    );
  }

  // 通讯录列表
  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title="用户列表"
        rightAction={
          <IconButton size="small" onClick={loadPeople}>
            <RefreshIcon />
          </IconButton>
        }
      />

      {!user && (
        <Alert severity="info" sx={{ mx: 2, mt: 1, mb: 0.5 }}>
          当前未登录，数据仅保存在浏览器缓存中，换设备或清理缓存后将丢失
        </Alert>
      )}

      {loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && peopleList.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 4, px: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            暂无记录，去排盘页面添加您的第一个八字吧
          </Typography>
          {!user && (
            <Button variant="contained" onClick={onLoginClick}>
              立即登录
            </Button>
          )}
        </Box>
      )}

      {!loading && peopleList.length > 0 && (
        <List dense>
          {peopleList.map((person) => {
            const birthDate = person.birthday_str
              ? dayjs(person.birthday_str).format("YYYY年M月D日")
              : "";
            return (
              <Box key={person.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleSelectPerson(person)}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: "#fff",
                          color: person.ri_gan
                            ? getGanZhiColor(person.ri_gan)
                            : "primary.main",
                          fontSize: "1.2rem",
                          fontWeight: "bold",
                          border: "1px solid",
                          borderColor: "primary.main",
                        }}
                      >
                        {person.ri_gan || <PersonIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {/* 中间：姓名 + 性别 + 日期 */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {person.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {person.gender}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {birthDate}
                        </Typography>
                      </Box>
                      {/* 右侧：八字四柱 */}
                      <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {person.nian_gan && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.nian_gan),
                                fontStyle: getIsYang(person.nian_gan)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.nian_gan}
                            </Typography>
                          )}
                          {person.yue_gan && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.yue_gan),
                                fontStyle: getIsYang(person.yue_gan)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.yue_gan}
                            </Typography>
                          )}
                          {person.ri_gan && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.ri_gan),
                                fontStyle: getIsYang(person.ri_gan)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.ri_gan}
                            </Typography>
                          )}
                          {person.shi_gan && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.shi_gan),
                                fontStyle: getIsYang(person.shi_gan)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.shi_gan}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {person.nian_zhi && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.nian_zhi),
                                fontStyle: getIsYang(person.nian_zhi)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.nian_zhi}
                            </Typography>
                          )}
                          {person.yue_zhi && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.yue_zhi),
                                fontStyle: getIsYang(person.yue_zhi)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.yue_zhi}
                            </Typography>
                          )}
                          {person.ri_zhi && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.ri_zhi),
                                fontStyle: getIsYang(person.ri_zhi)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.ri_zhi}
                            </Typography>
                          )}
                          {person.shi_zhi && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getGanZhiColor(person.shi_zhi),
                                fontStyle: getIsYang(person.shi_zhi)
                                  ? null
                                  : "italic",
                              }}
                            >
                              {person.shi_zhi}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {/* 更多操作按钮 */}
                      <IconButton
                        size="small"
                        edge="end"
                        sx={{ flexShrink: 0, ml: 0.5 }}
                        onClick={(e) => handleOpenMenu(e, person)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </Box>
            );
          })}
        </List>
      )}

      {/* 更多操作菜单 */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          编辑资料
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          删除人物
        </MenuItem>
      </Menu>

      {/* 编辑弹窗 */}
      <EditPersonDialog
        open={editDialogOpen}
        person={editPerson}
        user={user}
        onClose={() => setEditDialogOpen(false)}
        onSaved={loadPeople}
      />

      {/* 删除确认 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>确认删除？</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除「{deletePerson?.name}」的八字档案吗？此操作不可恢复。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
            color="inherit"
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? "删除中..." : "删除"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
